import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import useDataset from "datasets/hooks/useDataset";
import useDatasetTableFQNs from "datasets/hooks/useDatasetTableFQNs";
import TableTreeView from "datasets/pages/tables/components/TableTreeView";
import { TableAccessLevel } from "datasets/types/enums/access-level";
import { Search, Trash2 } from "lucide-react";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "~/components/ui/popover";
import useErrorToast from "~/contexts/error-toast";
import { apiMutate, getApiErrorMessage, isApiError } from "~/utils/apiClient";
import rumClient, { trackEvent } from "~/utils/monitoring";

const { VITE_CATALOG_API } = import.meta.env;

interface SnowflakeFormProps {
  accessLevel?: TableAccessLevel;
}

export const SnowflakeForm = ({ accessLevel }: SnowflakeFormProps) => {
  const navigate = useNavigate();
  const { datasetId } = useParams<{ datasetId: string }>();
  const { showError } = useErrorToast();
  const { mutate: mutateDataset } = useDataset();
  const { data: existingFqns, mutate: mutateFQNs } = useDatasetTableFQNs();

  const [popoverOpen, setPopoverOpen] = useState(false);
  const [tableSearch, setTableSearch] = useState("");
  const [tentativeSelections, setTentativeSelections] = useState<string[]>([]);
  const [selectedFqns, setSelectedFqns] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [validationError, setValidationError] = useState("");

  const handleCancel = () => {
    setTentativeSelections(selectedFqns);
    setTableSearch("");
    setPopoverOpen(false);
  };

  const handleSelect = () => {
    setSelectedFqns(tentativeSelections);
    setTableSearch("");
    setPopoverOpen(false);
    setValidationError("");
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      handleCancel();
    } else {
      setTentativeSelections(selectedFqns);
      setPopoverOpen(true);
    }
  };

  const removeSelected = (fqn: string) => {
    setSelectedFqns((prev) => prev.filter((f) => f !== fqn));
    setTentativeSelections((prev) => prev.filter((f) => f !== fqn));
  };

  const onSubmit = async () => {
    if (selectedFqns.length === 0) {
      setValidationError("Select at least one table.");
      return;
    }

    setSubmitting(true);
    try {
      const body = { tables: [...(existingFqns ?? []), ...selectedFqns] };

      await apiMutate(`${VITE_CATALOG_API}/datasets/${datasetId}`, "PATCH", body);

      trackEvent("dataset_snowflake_tables_added", {
        dataset_id: datasetId,
        count: selectedFqns.length,
      });

      mutateDataset();
      mutateFQNs();
      navigate(`/nextgen/datasets/${datasetId}/tables`);
    } catch (e: any) {
      rumClient?.recordError(e);
      showError(
        isApiError(e)
          ? getApiErrorMessage(e)
          : "There was a problem trying to add tables to dataset"
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col gap-5 max-w-lg">
      <div className="flex flex-col gap-2">
        <p className="text-sm font-medium">Tables</p>
        <Popover open={popoverOpen} onOpenChange={handleOpenChange}>
          <PopoverTrigger asChild>
            <div className="relative cursor-text" onClick={() => handleOpenChange(true)}>
              <Search className="absolute left-2.5 top-2.5 size-4 text-muted-foreground pointer-events-none" />
              <Input
                placeholder="Select tables and views..."
                value={tableSearch}
                onChange={(e) => setTableSearch(e.target.value)}
                className="pl-8"
                onFocus={() => {
                  if (!popoverOpen) {
                    handleOpenChange(true);
                  }
                }}
              />
            </div>
          </PopoverTrigger>
          <PopoverContent
            className="p-0 w-[660px]"
            align="start"
            onOpenAutoFocus={(e) => e.preventDefault()}
          >
            <div className="h-[400px] overflow-auto p-1">
              <TableTreeView
                searchTerm={tableSearch}
                selectedItems={tentativeSelections}
                setSelectedItems={setTentativeSelections}
                accessLevel={accessLevel}
              />
            </div>
            <div className="flex justify-end gap-2 border-t p-2">
              <Button type="button" variant="outline" size="sm" onClick={handleCancel}>
                Cancel
              </Button>
              <Button
                type="button"
                size="sm"
                disabled={!tentativeSelections.length}
                onClick={handleSelect}
              >
                Select
              </Button>
            </div>
          </PopoverContent>
        </Popover>
      </div>

      {selectedFqns.length > 0 && (
        <div className="flex flex-col gap-2">
          {selectedFqns.map((fqn) => (
            <div
              key={fqn}
              className="flex items-center justify-between rounded-xl bg-muted px-4 py-2"
            >
              <span className="text-sm">{fqn.replace(/^snowflake\./, "")}</span>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="size-7 text-muted-foreground hover:text-foreground"
                onClick={() => removeSelected(fqn)}
              >
                <Trash2 className="size-4" />
              </Button>
            </div>
          ))}
        </div>
      )}

      {validationError && <p className="text-sm text-destructive">{validationError}</p>}

      <Button
        type="button"
        onClick={onSubmit}
        disabled={submitting || !selectedFqns.length}
        className="w-fit"
      >
        {submitting ? "Adding…" : selectedFqns.length > 1 ? "Add Tables" : "Add Table"}
      </Button>
    </div>
  );
};
